export interface ChWireQuestion {
  type: string;
  name: string;
  title?: string;
  isRequired?: boolean;
  choices?: Array<string | { value: string; text: string }>;
  inputType?: string;
  visibleIf?: string;
  display?: { label: string; format: string };
  problemCode?: string;
  rateMin?: number;
  rateMax?: number;
  html?: string;
  elements?: ChWireQuestion[];
}

function wireRaw(el: ChWireQuestion): Record<string, unknown> {
  return el as unknown as Record<string, unknown>;
}

/** API / Newtonsoft may send PascalCase (`Type`, `VisibleIf`, `Elements`). */
export function wireElementType(el: ChWireQuestion): string {
  const r = wireRaw(el);
  const t = el.type ?? r.Type;
  return typeof t === "string" ? t.trim().toLowerCase() : "";
}

export function wireName(el: ChWireQuestion): string {
  const r = wireRaw(el);
  const n = el.name ?? r.Name;
  return typeof n === "string" ? n : "";
}

export function wireTitle(el: ChWireQuestion): string {
  const r = wireRaw(el);
  const t = el.title ?? r.Title;
  if (typeof t === "string" && t.trim()) return t;
  return wireName(el);
}

export function wireHtml(el: ChWireQuestion): string {
  const r = wireRaw(el);
  const h = el.html ?? r.Html ?? r.HTML;
  return typeof h === "string" ? h : "";
}

/** Prefer `problemCode` for answer persistence (matches PortalConfigAnswers); else `name`. */
export function getWireAnswerKey(el: ChWireQuestion): string {
  const r = wireRaw(el);
  const pc = el.problemCode ?? r.ProblemCode;
  if (typeof pc === "string" && pc.trim()) return pc.trim();
  return wireName(el);
}

export function getWireVisibleIf(el: ChWireQuestion): string | undefined {
  const r = wireRaw(el);
  const v = el.visibleIf ?? r.VisibleIf;
  if (typeof v !== "string" || !v.trim()) return undefined;
  return v.trim();
}

export function wireChildElements(el: ChWireQuestion): ChWireQuestion[] {
  const r = wireRaw(el);
  const arr = el.elements ?? r.Elements;
  return Array.isArray(arr) ? (arr as ChWireQuestion[]) : [];
}

export function wireChoicesRaw(el: ChWireQuestion): ChWireQuestion["choices"] {
  const r = wireRaw(el);
  return (el.choices ?? r.Choices) as ChWireQuestion["choices"];
}

export function isPanel(el: ChWireQuestion): boolean {
  return wireElementType(el) === "panel";
}

export function isFollowUpPanel(el: ChWireQuestion): boolean {
  if (!isPanel(el)) return false;
  const n = wireName(el).toLowerCase();
  return n.includes("_followup") || n.includes("followup");
}

export function normalizeChoices(
  choices?: Array<string | { value: string; text: string }>
): { value: string; text: string }[] {
  if (!choices?.length) return [];
  return choices.map((c) =>
    typeof c === "string" ? { value: c, text: c } : { value: c.value, text: c.text ?? c.value }
  );
}
